import { observable, action, runInAction } from 'mobx';
import { DisposableBase } from '../Types';
import { Api, type FaceEdgeRecognizeWithOptions } from '../Clients/Identities/Api';
import { identifyPerson, type PersonCard } from '../Clients/Identities/identifyPerson';
import { bitmapToBase64Jpeg } from '../Utils/imageEncoding';
import { awaitClassification } from './awaitClassification';
import { resolveOutcome, type GuardOutcome } from '../Components/Guard/resolveOutcome';
import { speakMessage } from '../Utils/speakMessage';
import type { SettingsStore } from '../Setup/Stores/SettingsStore';
import type { IdentitiesEventsStore } from './IdentitiesEventsStore';
import type { EventThemesStore } from './EventThemesStore';
import type { FaceSdkStore } from './FaceSdkStore';
import { debugLog } from './DebugLogStore';

const CLASSIFICATION_TIMEOUT_MS = 3000;

// Paravision gen5 crop-aligned recognition expects a 112×112 ArcFace crop.
const RECOGNIZE_ALIGN_SIZE = 112;

// The IN/OUT detection is submitted 1s after the Neutral detection to work
// around a server-side collision when two detections share a timestamp.
const RECORD_TIME_OFFSET_MS = 1000;

export interface GuardResult {
    outcome: GuardOutcome;
    classificationValue: string | null;
    person: PersonCard | null;
}

export class GuardModeStore extends DisposableBase {
    private readonly _settings: SettingsStore;
    private readonly _events: IdentitiesEventsStore;
    private readonly _themes: EventThemesStore;
    private readonly _faceSdkStore: FaceSdkStore;

    private _api: Api<unknown> | null = null;
    private _apiBaseUrl: string | null = null;

    // Captured at scan time; reused when the guard records an IN/OUT detection.
    private _pendingExpanded: string | null = null;
    private _pendingAuthToken: string | null = null;
    private _pendingDetectTimeMs: number | null = null;

    @observable accessor result: GuardResult | null = null;
    @observable accessor resultVisible = false;

    constructor(settings: SettingsStore, events: IdentitiesEventsStore, themes: EventThemesStore, faceSdkStore: FaceSdkStore) {
        super();
        this._settings = settings;
        this._events = events;
        this._themes = themes;
        this._faceSdkStore = faceSdkStore;
    }

    /** The detector the IN button records under (first configured IN detector). */
    get inDetector(): string | null {
        return this._settings.guardInDetectors[0] ?? null;
    }

    /** The detector the OUT button records under (first configured OUT detector). */
    get outDetector(): string | null {
        return this._settings.guardOutDetectors[0] ?? null;
    }

    private _getApi(): Api<unknown> {
        const baseUrl = this._settings.identitiesSetup.serverAddress.trim();
        if (!this._api || this._apiBaseUrl !== baseUrl) {
            this._api = new Api({ baseUrl });
            this._apiBaseUrl = baseUrl;
        }
        return this._api;
    }

    /** Injected into FaceServiceStore.submissionHandler when appMode === 'guard'. */
    handleGuardCapture = async (image: ImageBitmap, _subjectId: string): Promise<void> => {
        const { identitiesSetup } = this._settings;
        if (!identitiesSetup.isConnected) {
            debugLog.error('Guard: not connected to identities service');
            return;
        }
        const authToken = await identitiesSetup.getLatestToken();
        if (!authToken) {
            debugLog.error('Guard: no auth token');
            return;
        }
        const api = this._getApi();
        const fields = {
            vendor: this._settings.guardVendorField,
            email: this._settings.guardEmailField,
            phone: this._settings.guardPhoneField,
            dob: this._settings.guardDobField,
        };
        // Detection timestamp for the Neutral publish; the IN/OUT record reuses it + 1s.
        const detectTime = new Date();
        const detectTimeIso = detectTime.toISOString();

        try {
            // Encode once; reused by the Neutral publish, the person lookup, and the IN/OUT record.
            const expanded = await bitmapToBase64Jpeg(image);

            // Theme (Neutral publish) + person lookup, concurrently. The theme promise
            // must not reject — otherwise Promise.all would discard an already-resolved
            // person (and leak its photo blob URL).
            const themePromise = this._publishNeutral(api, authToken, expanded, detectTimeIso).catch((e) => {
                debugLog.error(`Guard neutral publish failed: ${e instanceof Error ? e.message : String(e)}`);
                return null;
            });
            const personPromise = (async (): Promise<{ person: PersonCard | null; failed: boolean }> => {
                const faceSdk = this._faceSdkStore.faceSdk;
                if (!faceSdk) return { person: null, failed: false };
                let aligned: ImageBitmap | null = null;
                const detected = await faceSdk.getFaces(image, true);
                try {
                    if (detected.length === 0) return { person: null, failed: false };
                    const face = detected[0];
                    await faceSdk.getLandmarks([face]);
                    if (!face.landmarks) return { person: null, failed: false };
                    aligned = await faceSdk.alignFace(face, RECOGNIZE_ALIGN_SIZE);
                    const croppedAlignedB64 = await bitmapToBase64Jpeg(aligned);
                    const person = await identifyPerson(api, authToken, croppedAlignedB64, fields);
                    return { person, failed: false };
                } catch (e) {
                    debugLog.error(`Guard lookup failed: ${e instanceof Error ? e.message : String(e)}`);
                    return { person: null, failed: true };
                } finally {
                    if (aligned) { try { aligned.close(); } catch { /* already closed */ } }
                    // Close detection-created bitmaps to avoid leaks; do NOT close `image` (owned by FaceServiceStore).
                    for (const f of detected) {
                        const fr = f as typeof f & { preparedImage?: { input?: ImageBitmap }; aligned?: ImageBitmap };
                        if (fr.preparedImage?.input && fr.preparedImage.input !== image) { try { fr.preparedImage.input.close(); } catch { /* noop */ } }
                        if (fr.aligned && fr.aligned !== image) { try { fr.aligned.close(); } catch { /* noop */ } }
                        if (f.sourceImage && f.sourceImage !== image) { try { f.sourceImage.close(); } catch { /* noop */ } }
                    }
                }
            })();

            const [classificationValue, lookup] = await Promise.all([themePromise, personPromise]);

            const matched = !!lookup.person;
            const theme = classificationValue ? this._themes.getThemeByClassification(classificationValue) : null;
            const { outcome } = resolveOutcome({
                matched,
                hasTheme: !!classificationValue,
                deniesAccess: theme?.deniesAccess ?? false,
                lookupFailed: lookup.failed,
            });

            // Stash what the IN/OUT record needs; the result stays up until the guard
            // taps In / Ignore / Out (no auto-dismiss).
            this._pendingExpanded = expanded;
            this._pendingAuthToken = authToken;
            this._pendingDetectTimeMs = detectTime.getTime();

            runInAction(() => {
                this.result = { outcome, classificationValue, person: lookup.person };
                this.resultVisible = true;
            });

            if (this._settings.guardSoundEnabled && theme?.speechMessage) {
                void speakMessage(theme.speechMessage);
            }
        } catch (e) {
            debugLog.error(`Guard capture error: ${e instanceof Error ? e.message : String(e)}`);
            this._clearPending();
            runInAction(() => {
                this.result = { outcome: 'no-result', classificationValue: null, person: null };
                this.resultVisible = true;
            });
        }
    };

    /** Records the detection under the IN detector, then dismisses the result. */
    recordIn = () => { void this._recordAndDismiss(this.inDetector); };

    /** Records the detection under the OUT detector, then dismisses the result. */
    recordOut = () => { void this._recordAndDismiss(this.outDetector); };

    /** Dismisses the result without recording an IN/OUT detection. */
    ignore = () => { this.dismissResult(); };

    private async _recordAndDismiss(detectorName: string | null) {
        // Capture pending context before dismiss clears it; dismiss first for snappy UX.
        const api = this._api;
        const expanded = this._pendingExpanded;
        const authToken = this._pendingAuthToken;
        const baseMs = this._pendingDetectTimeMs;

        this.dismissResult();

        if (!detectorName || !api || !expanded || !authToken || baseMs == null) return;
        try {
            const detectTimeIso = new Date(baseMs + RECORD_TIME_OFFSET_MS).toISOString();
            await this._publishDetectorRaw(api, authToken, expanded, detectorName, detectTimeIso);
        } catch (e) {
            debugLog.error(`Guard record failed: ${e instanceof Error ? e.message : String(e)}`);
        }
    }

    private async _publishNeutral(api: Api<unknown>, authToken: string, expanded: string, detectTimeIso: string): Promise<string | null> {
        const detectorName = this._settings.guardNeutralDetector.trim();
        if (!detectorName) {
            debugLog.error('Guard: no Neutral detector configured');
            return null;
        }
        const response = await this._publishDetectorRaw(api, authToken, expanded, detectorName, detectTimeIso);
        const detectId = response?.detectedFaces?.[0]?.detectId;
        if (!detectId) return null;
        return awaitClassification(this._events.faceDetectionAttributes, detectId, CLASSIFICATION_TIMEOUT_MS);
    }

    private async _publishDetectorRaw(api: Api<unknown>, authToken: string, expanded: string, detectorName: string, detectTimeIso: string) {
        const payload: FaceEdgeRecognizeWithOptions = {
            croppedAligned: null,
            expanded,
            embeddings: null,
            recognitionModel: null,
            detectTimeUtc: detectTimeIso,
            ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            detectorName,
            largestFaceOnly: true,
            allowDoubleDetection: true,
        };
        return api.api.faceEdgePublishWithResponse(payload, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
    }

    private _clearPending() {
        this._pendingExpanded = null;
        this._pendingAuthToken = null;
        this._pendingDetectTimeMs = null;
    }

    @action dismissResult = () => {
        const url = this.result?.person?.enrollmentPhotoUrl;
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
        this._clearPending();
        this.resultVisible = false;
        this.result = null;
    };

    dispose() {
        super.dispose();
        // Revoke any live photo blob URL + clear pending state.
        this.dismissResult();
        this._api = null;
    }
}
